import { Calendar } from "@/components/ui/calendar";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Select } from "@/components/ui/select";
import { DOBMonths, DOBYears, getMonthName } from "@/lib/date-utils";
import { useEffect, useMemo, useState } from "react";

type DOBCalendarProps = {
  onDateChange: (date?: Date) => void;
  selectedDate?: Date;
  minDate?: Date;
  maxDate?: Date;
};

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const DOBCalendar = ({
  onDateChange,
  selectedDate,
  minDate,
  maxDate,
}: DOBCalendarProps) => {
  const today = startOfDay(new Date());
  const defaultMin = useMemo(
    () => (minDate ? startOfDay(minDate) : new Date(1900, 0, 1)),
    [minDate],
  );
  const rawMax = maxDate ?? new Date();
  const rawMaxDay = startOfDay(rawMax);
  const defaultMax = rawMaxDay > today ? today : rawMaxDay;

  const [year, setYear] = useState<number>(
    selectedDate?.getFullYear() ?? defaultMax.getFullYear(),
  );
  const [month, setMonth] = useState<string>(
    selectedDate
      ? getMonthName(selectedDate.getMonth())
      : getMonthName(defaultMax.getMonth()),
  );
  const [date, setDate] = useState<Date | undefined>(selectedDate);

  useEffect(() => {
    if (selectedDate) {
      setYear(selectedDate.getFullYear());
      setMonth(getMonthName(selectedDate.getMonth()));
      setDate(selectedDate);
    } else {
      setDate(undefined);
    }
  }, [selectedDate]);

  const filteredYears = useMemo(() => {
    const minY = defaultMin.getFullYear();
    const maxY = defaultMax.getFullYear();
    return DOBYears.filter((y) => y >= minY && y <= maxY);
  }, [defaultMin, defaultMax]);

  const filteredMonths = useMemo(() => {
    const minY = defaultMin.getFullYear();
    const maxY = defaultMax.getFullYear();
    return DOBMonths.filter((_, i) => {
      if (year === minY && year === maxY)
        return i >= defaultMin.getMonth() && i <= defaultMax.getMonth();
      if (year === minY) return i >= defaultMin.getMonth();
      if (year === maxY) return i <= defaultMax.getMonth();
      return true;
    });
  }, [year, defaultMin, defaultMax]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row space-x-2">
        <Select
          value={year.toString()}
          onValueChange={(value) => setYear(parseInt(value))}
        >
          <SelectTrigger>{year}</SelectTrigger>

          <SelectContent className="z-[150]">
            {filteredYears.map((y) => (
              <SelectItem key={y} value={y.toString()}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={(value) => setMonth(value)}>
          <SelectTrigger>{month}</SelectTrigger>

          <SelectContent className="z-[150]">
            {filteredMonths.map((m) => (
              <SelectItem key={m} value={m.toString()}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {year && month && (
        <Calendar
          mode="single"
          selected={date}
          onSelect={(newDate) => {
            setDate(newDate);
            onDateChange(newDate);
          }}
          month={new Date(year, DOBMonths.indexOf(month), 1)}
          onMonthChange={(newMonth) => {
            setYear(newMonth.getFullYear());
            setMonth(getMonthName(newMonth.getMonth()));
          }}
          disabled={(d) => {
            const day = startOfDay(d);
            return day < startOfDay(defaultMin) || day > startOfDay(defaultMax);
          }}
        />
      )}
    </div>
  );
};

export default DOBCalendar;
